import React, { useEffect, useState } from "react";
import { MapPin, Heart, Leaf, ArrowRight, Phone, Mail, Globe, BookOpen, Coffee, Home, Sparkles, Target, Baby, Flame, Users, Brain, Flower2, Droplets, Smile, Book, CheckCircle, Badge, Calendar, Zap, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import NavBar from '@/components/NavBar';
import { ReturnToTop } from '@/components/ReturnToTop';
import ScrollDownArrow from '@/components/ScrollDownArrow';
import { BOOKING_URL } from '@/constants/urls';
import treatmentImage from '@/assets/treatment.jpg';
import OfferPopup from '@/components/OfferPopup';

const SkinHealthPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showOfferPopup, setShowOfferPopup] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.title = "Skin Health Currimundi | Chinese Medicine";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Natural skin health and holistic skincare with Chinese Medicine in Currimundi. Treat acne, eczema, psoriasis & skin conditions. Book online or visit our Sunshine Coast clinic.");
    }
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Skin Health Currimundi | Chinese Medicine");
    }
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Natural skin health and holistic skincare with Chinese Medicine in Currimundi. Treat acne, eczema, psoriasis & skin conditions. Book online or visit our Sunshine Coast clinic.");
    }
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', "https://womenandtheway.com.au/skin-health");
    }
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', "https://womenandtheway.com.au/hero_image.jpg");
    }
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', "Skin Health Currimundi | Chinese Medicine");
    }
    const schema = {
      "@context": "https://schema.org",
      "@type": "MedicalClinic",
      "name": "Women and The Way - Skin Health with Chinese Medicine",
      "image": "https://womenandtheway.com.au/hero_image.jpg",
      "description": "Natural skin health and holistic skincare with Chinese Medicine in Currimundi. Treat acne, eczema, psoriasis & skin conditions.",
      "url": "https://womenandtheway.com.au/skin-health",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "700 Nicklin Way",
        "addressLocality": "Currimundi",
        "addressRegion": "QLD",
        "postalCode": "4551",
        "addressCountry": "AU"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": -26.5270,
        "longitude": 153.0890
      },
      "priceRange": "$$",
      "telephone": "0415537784",
      "email": "womenandtheway@gmail.com",
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "17:00"
      },
      "serviceType": "Skin Health, Holistic Skincare, Acne Treatment, Eczema Relief, Chinese Medicine, Acupuncture",
      "areaServed": {
        "@type": "Place",
        "name": "Sunshine Coast, Queensland, Australia"
      },
      "sameAs": [
        "https://www.facebook.com/Womenandtheway",
        "https://www.instagram.com/women_and_the_way"
      ]
    };
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) existingSchema.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { if (script.parentNode) script.parentNode.removeChild(script); };
  }, []);

  const handleBooking = async () => {
    setIsLoading(true);
    try {
      window.open(BOOKING_URL, '_blank');
    } catch (error) {
      console.error('Booking error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-white via-white to-amber-50">
        <NavBar />
        {/* Hero Section */}
        <section className="relative py-20 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                  Chinese Medicine for Skin Health
                </h1>
                <p className="text-xl md:text-2xl text-sage-600 mb-6 font-medium">
                  Heal your skin from within - with ancient wisdom and gentle care.
                </p>
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  Your skin is a reflection of your internal health and wellbeing. At Women and the Way, we address skin concerns from the root cause, using Chinese Medicine to restore balance and vitality from the inside out.
                </p>
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  Whether you're dealing with acne, eczema, rosacea, or simply want to maintain healthy, glowing skin, our treatments help restore your skin's natural balance. Our approach combines the wisdom of Traditional Chinese Medicine with modern understanding of skin health. Many of our clients also benefit from our <a href="/cosmetic-acupuncture" className="text-sage-600 hover:text-sage-800 underline transition-colors">cosmetic acupuncture</a> and <a href="/digestion-gut-health" className="text-sage-600 hover:text-sage-800 underline transition-colors">digestive health services</a>.
                </p>
                <Button 
                  size="lg" 
                  className="bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                  onClick={handleBooking}
                  disabled={isLoading}
                >
                  {isLoading ? 'Booking...' : 'Book Now'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
              <div className="relative">
                <img 
                  src={treatmentImage} 
                  alt="Chinese medicine for skin health, acne, eczema, and holistic skin care" 
                  className="rounded-2xl shadow-lg w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>
        {/* Why Chinese Medicine for Skin? */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6 flex items-center justify-center gap-3">
                <Droplets className="w-8 h-8 text-sage-600" />
                Why Chinese Medicine for Skin?
              </h2>
            </div>
            <div className="bg-white rounded-2xl shadow-lg border-2 border-amber-200/50 p-8 md:p-12">
              <p className="text-lg text-sage-700 mb-6 leading-relaxed">
                Skin reflects what's happening internally - especially in the organs that govern digestion, heat, and detoxification: the Spleen, Liver, Lungs, and Kidneys. Inflammation, dampness, heat, and deficiency can all present as breakouts, rashes, redness, or dryness.
              </p>
              <p className="text-lg text-sage-700 leading-relaxed">
                Rather than suppressing the skin's signals, Chinese medicine helps rebalance the systems that created the issue - bringing long-term resolution and calm.
              </p>
            </div>
          </div>
        </section>
        {/* What We Offer */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                What We Offer
              </h2>
              <p className="text-xl text-sage-600 mb-8">
                Holistic Skin Support In-Clinic or Online
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Home className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">In-Clinic Care</h3>
                  </div>
                  <p className="text-sage-700 mb-4">
                    Come into our Currimundi clinic for targeted, nourishing treatments that address the internal roots of your skin concerns.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🪡</span>
                      <p className="text-sage-700">Acupuncture to reduce inflammation, support digestion, and regulate hormones</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🌿</span>
                      <p className="text-sage-700">Chinese herbal medicine tailored to your specific skin presentation and constitutional pattern</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🌀</span>
                      <p className="text-sage-700">Digestive and stress support - because what's happening in the gut and nervous system shows up on the skin</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">💆‍♀️</span>
                      <p className="text-sage-700">Cosmetic acupuncture or gua sha for local circulation and collagen support (if appropriate)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Globe className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Online Consults</h3>
                  </div>
                  <p className="text-sage-700 mb-4 font-medium">
                    We can support you remotely with the same depth of care.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-lg">💬</span>
                      <p className="text-sage-700">Telehealth consults to assess your skin pattern, diet, emotions, and triggers</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🌿</span>
                      <p className="text-sage-700">Custom herbal formulas and skincare rituals mailed to your door</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">📚</span>
                      <p className="text-sage-700">Lifestyle and food recommendations aligned with your Chinese medicine diagnosis</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
        {/* Conditions We Commonly Support */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                Conditions We Commonly Support
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { western: "Acne (hormonal, cystic, inflammatory)", chinese: "Heat in Lung/Stomach, Damp-Heat, Blood Heat, Liver Qi stagnation" },
                { western: "Rosacea", chinese: "Stomach Heat, Yin deficiency, Liver Fire" },
                { western: "Eczema", chinese: "Wind-Damp, Blood deficiency, Heat in the Blood" },
                { western: "Psoriasis", chinese: "Blood Heat with dryness, Liver constraint" },
                { western: "Perioral dermatitis", chinese: "Spleen deficiency, Dampness, Heat toxicity" },
                { western: "Dry, flaky, or reactive skin", chinese: "Blood and Yin deficiency, Spleen Qi deficiency" },
                { western: "Skin affected by cycle/hormones", chinese: "Liver Qi stagnation, Blood deficiency, Kidney imbalance" }
              ].map((condition, index) => (
                <Card key={index} className="bg-white border-2 border-amber-200/50 shadow-sm rounded-xl hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-sage-800 mb-2">{condition.western}</h3>
                    <p className="text-sage-600 text-sm italic">{condition.chinese}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
        {/* What Makes Our Skin Care Different */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                What Makes Our Approach Different
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { icon: "🫶", title: "Inside-out healing", desc: "We address the organs, patterns, and emotional stress behind your skin - so results last" },
                { icon: "🌿", title: "Custom herbs and natural approaches", desc: "No harsh chemicals. No one-size-fits-all. Just thoughtful formulas and calming rituals" },
                { icon: "📚", title: "Cycle-aware skin care", desc: "We tune into how your hormones and menstrual phases affect your skin" },
                { icon: "💆‍♀️", title: "Beauty with purpose", desc: "When needed, we combine cosmetic acupuncture, gua sha, and internal medicine for both function and glow" }
              ].map((feature, index) => (
                <Card key={index} className="bg-white border-2 border-amber-200/50 shadow-sm rounded-xl hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 text-center">
                    <div className="text-3xl mb-3">{feature.icon}</div>
                    <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">{feature.title}</h3>
                    <p className="text-sage-700">{feature.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
        {/* Our Tools for Skin Support */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                Our Tools for Skin Support
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Coffee, title: "Herbal teas, capsules, and tinctures", desc: "Tailored to your pattern" },
                { icon: Droplets, title: "Acupressure and gua sha", desc: "For lymphatic support" },
                { icon: Leaf, title: "Topical herbal pastes", desc: "Or gentle recommendations for product selection" },
                { icon: Book, title: "Diet and lifestyle guides", desc: "For cooling inflammation and supporting gut health" },
                { icon: BookOpen, title: "Recommended reading", desc: "Skin Deep, Healing with Whole Foods, Period Repair Manual" }
              ].map((tool, index) => (
                <Card key={index} className="bg-white border-2 border-amber-200/50 shadow-sm rounded-xl hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 text-center">
                    <tool.icon className="w-8 h-8 text-sage-600 mx-auto mb-3" />
                    <h3 className="font-serif text-lg font-semibold text-sage-800 mb-2">{tool.title}</h3>
                    <p className="text-sage-700 text-sm">{tool.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
        {/* CTA Section */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
              Book Your Session
            </h2>
            <p className="text-xl text-sage-700 mb-8">
              Your skin is speaking - and we're here to listen. Let's uncover the root causes, calm the surface, and help you feel at home in your skin again.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <MapPin className="w-5 h-5 text-sage-600" />
                <a 
                  href="https://maps.google.com/?q=700+Nicklin+Way+Currimundi+QLD" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-sage-800 transition-colors"
                >
                  700 Nicklin Way, Currimundi
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Phone className="w-5 h-5 text-sage-600" />
                <a 
                  href="tel:0415537784" 
                  className="hover:text-sage-800 transition-colors"
                >
                  0415 537 784
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Mail className="w-5 h-5 text-sage-600" />
                <a 
                  href="mailto:womenandtheway@gmail.com" 
                  className="hover:text-sage-800 transition-colors"
                >
                  womenandtheway@gmail.com
                </a>
              </div>
            </div>
            <Button 
              size="lg" 
              className="w-full sm:w-auto text-center break-words bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 mx-auto"
              onClick={handleBooking}
              disabled={isLoading}
            >
              {isLoading ? 'Booking...' : 'Book Your Session'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </section>
        <ScrollDownArrow />
        <ReturnToTop />
      </div>
      <OfferPopup 
        isOpen={showOfferPopup}
        onClose={() => setShowOfferPopup(false)}
        offerType="intro-skin"
        showOnPages={['/skin-health']}
        delay={5000}
      />
    </>
  );
};

export default SkinHealthPage; 